/**
 * Equity Portfolio Engine
 *
 * Pure calculation layer for the standalone Equity Calculator. Unlike the
 * job-offer comparison engine (which scores up to three competing offers), this
 * engine models a single person's *equity portfolio*: every grant they hold or
 * have been offered, projected against one shared price path and aggregated into
 * a year-by-year value timeline.
 *
 * It reuses the shared primitives in `equity-core.ts` (tax presets, vesting
 * schedule, formatters) so the math stays consistent with the job-offer tool.
 *
 * Supported instruments: RSUs, stock options (ISO/NSO), ESPP, restricted stock
 * awards (RSAs, with 83(b)), and SARs / phantom / performance share units (PSUs).
 *
 * ── Tax model (documented, intentionally pragmatic) ───────────────────────────
 * "Net if liquidated at year Y" is the after-tax wealth from selling everything
 * vested by year Y at that year's price. The conventions:
 *   • Full-value grants (RSU, RSA, PSU): ordinary tax is due on fair-market value
 *     at vest; later appreciation above that basis is long-term capital gains.
 *     Ordinary tax for grants that vest *within* the projection window is counted
 *     as a future cost; for shares already vested at t0 it is treated as historical
 *     and excluded from a "sell today" figure.
 *   • Appreciation grants (options, SARs): only the spread above strike/baseline
 *     has value. NSOs and SAR/phantom payouts are ordinary; ISOs held for LTCG are
 *     taxed at capital-gains rates (QSBS-eligible ISOs use the state/city-only rate
 *     to approximate the §1202 federal exemption).
 *   • ESPP: the purchase discount (bargain element) is ordinary income at sale and
 *     the remaining appreciation is LTCG; qualifying vs disqualifying disposition
 *     changes how much of the gain is ordinary.
 * This favors transparency over exhaustive edge-case fidelity (AMT timing,
 * sell-to-cover share reduction, and >$10M QSBS caps are approximated).
 */

import {
  buildVestSchedule,
  resolveTaxRates,
  type FilingStatus,
  type RsuVestFrequency,
  type TaxPresetKey,
  type TaxRates
} from './equity-core.ts';

// ═══════════════════════════════════════════════════════════════════
// INPUT SCHEMA
// ═══════════════════════════════════════════════════════════════════

export type InstrumentKind = 'rsu' | 'option' | 'espp' | 'rsa' | 'sar';
export type OptionType = 'iso' | 'nso';
/** 'appreciation' = SAR / phantom (spread only); 'full-value' = PSU (share value). */
export type SarSettlement = 'appreciation' | 'full-value';

interface HoldingBase {
  id: string;
  label: string;
  color: string;
}

/** Schedule fields shared by grant-style instruments. */
interface VestingFields {
  vestYears: number;
  cliffMonths: number;
  vestFrequency: RsuVestFrequency;
}

export interface RsuHolding extends HoldingBase, VestingFields {
  kind: 'rsu';
  /** Shares already vested and held today. */
  vestedShares: number;
  /** FMV per share taxed at the prior vest (cost basis) for the already-vested portion. */
  vestedBasisPerShare: number;
  /** Shares not yet vested; spread over the schedule from today. */
  unvestedShares: number;
}

export interface OptionHolding extends HoldingBase, VestingFields {
  kind: 'option';
  optionType: OptionType;
  strike: number;
  vestedShares: number;
  unvestedShares: number;
  /** ISO held ≥1yr for qualifying LTCG treatment (ignored for NSO). */
  holdForLtcg: boolean;
  /** QSBS §1202-eligible (early-exercised ISO, ≥5yr): approximates federal exemption. */
  qsbsEligible: boolean;
}

export interface EsppHolding extends HoldingBase {
  kind: 'espp';
  /** Dollars contributed per purchase period. */
  contributionPerPeriod: number;
  /** Purchase periods per year (e.g. 2 for semiannual). */
  periodsPerYear: number;
  /** Discount off purchase price, e.g. 0.15 for 15%. */
  discountPct: number;
  /** Lookback provision: purchase price uses the lower of offering/purchase FMV. */
  lookback: boolean;
  /** 1-based first/last contribution years (inclusive). */
  startYear: number;
  endYear: number;
  /** Assume a qualifying disposition at liquidation (vs disqualifying). */
  qualifying: boolean;
}

export interface RsaHolding extends HoldingBase, VestingFields {
  kind: 'rsa';
  totalShares: number;
  /** Amount paid per share at grant (often near $0 for founders). */
  purchasePricePerShare: number;
  /** FMV per share at grant. */
  grantPricePerShare: number;
  /** 83(b) election: recognize ordinary income at grant, then all gain is LTCG. */
  election83b: boolean;
}

export interface SarHolding extends HoldingBase, VestingFields {
  kind: 'sar';
  settlement: SarSettlement;
  /** Baseline (strike-equivalent) price for appreciation settlement. */
  baselinePrice: number;
  targetShares: number;
  /** Payout multiplier on target shares (1.0 = target, 2.0 = max). */
  performanceMultiplier: number;
}

export type Holding =
  | RsuHolding
  | OptionHolding
  | EsppHolding
  | RsaHolding
  | SarHolding;

export interface PortfolioSettings {
  /** Years projected forward (1..N). */
  horizon: number;
  /** Today's price per share / unit. */
  currentPrice: number;
  /** Expected annual price growth (decimal, e.g. 0.1 for 10%). */
  annualGrowthPct: number;
  taxPreset: TaxPresetKey;
  filingStatus: FilingStatus;
  customRates?: TaxRates;
}

// ═══════════════════════════════════════════════════════════════════
// RESULT TYPES
// ═══════════════════════════════════════════════════════════════════

export interface HoldingYear {
  yr: number;
  price: number;
  vestedShares: number;
  unvestedShares: number;
  /** Pre-tax value of vested holdings at this year's price. */
  vestedGross: number;
  /** Pre-tax value of still-unvested holdings at this year's price. */
  unvestedGross: number;
  /** After-tax cash if vested holdings were liquidated at this year's price. */
  vestedNet: number;
  /** Ordinary income tax recognized at vest events landing in this year. */
  ordinaryTaxThisYr: number;
}

export interface HoldingProjection {
  id: string;
  label: string;
  kind: InstrumentKind;
  color: string;
  totalShares: number;
  years: HoldingYear[];
}

export interface PortfolioYear {
  yr: number;
  price: number;
  vestedGross: number;
  unvestedGross: number;
  totalGross: number;
  vestedNet: number;
  ordinaryTaxThisYr: number;
}

export interface PortfolioResult {
  horizon: number;
  currentPrice: number;
  annualGrowthPct: number;
  rates: TaxRates;
  holdings: HoldingProjection[];
  portfolio: PortfolioYear[];
}

// ═══════════════════════════════════════════════════════════════════
// INTERNAL: vest tranches
// ═══════════════════════════════════════════════════════════════════

/**
 * A block of shares that vests in a given year, with the tax treatment needed to
 * value it and compute after-tax proceeds. ESPP lots carry extra purchase detail.
 */
interface Tranche {
  vestYr: number;
  shares: number;
  /** For full-value grants: FMV-at-vest basis. For appreciation: strike/baseline. */
  basisPerShare: number;
  fullValue: boolean;
  /** Per-share ordinary tax recognized at vest (full-value grants only). */
  ordinaryPerShareAtVest: number;
  /** Whether ordinaryPerShareAtVest is counted as a future cost (vestYr ≥ 1). */
  countVestOrdinary: boolean;
  /** Rate applied to appreciation above basis at sale (full-value grants). */
  gainRate: number;
  /** Appreciation grants where the whole spread is ordinary (NSO, SAR/phantom). */
  intrinsicOrdinary: boolean;
  /** Rate for appreciation grants (ordinary or capital-gains depending on flag). */
  intrinsicRate: number;
  /** ESPP-specific purchase detail; when set, ESPP tax rules apply. */
  espp?: {
    purchasePrice: number;
    purchaseFmv: number;
    offeringPrice: number;
    discountPct: number;
    qualifying: boolean;
  };
}

interface BuiltHolding {
  tranches: Tranche[];
  /** One-off ordinary income events (e.g. 83(b) at grant), keyed by year. */
  ordinaryEvents: Array<{ yr: number; amount: number }>;
}

// ═══════════════════════════════════════════════════════════════════
// INTERNAL: per-tranche valuation
// ═══════════════════════════════════════════════════════════════════

function trancheGross(tr: Tranche, price: number): number {
  return tr.fullValue ? tr.shares * price : tr.shares * Math.max(0, price - tr.basisPerShare);
}

function trancheNet(tr: Tranche, price: number, rates: TaxRates): number {
  if (tr.espp) {
    const { purchasePrice, purchaseFmv, offeringPrice, discountPct, qualifying } = tr.espp;
    let ordinaryPerShare: number;
    let ltcgGainPerShare: number;
    if (qualifying) {
      // Qualifying: ordinary = lesser of the offering-date discount and the actual
      // gain; the remainder is long-term capital gain.
      ordinaryPerShare = Math.min(discountPct * offeringPrice, Math.max(0, price - purchasePrice));
      ltcgGainPerShare = Math.max(0, price - purchasePrice - ordinaryPerShare);
    } else {
      // Disqualifying: bargain element (FMV − purchase price) is ordinary; gain above
      // purchase-date FMV is LTCG.
      ordinaryPerShare = Math.max(0, purchaseFmv - purchasePrice);
      ltcgGainPerShare = Math.max(0, price - purchaseFmv);
    }
    const netPerShare = price - ordinaryPerShare * rates.ord - ltcgGainPerShare * rates.ltcg;
    return tr.shares * netPerShare;
  }

  if (tr.fullValue) {
    const ordCost = tr.countVestOrdinary ? tr.ordinaryPerShareAtVest : 0;
    const gain = Math.max(0, price - tr.basisPerShare);
    return tr.shares * (price - ordCost - gain * tr.gainRate);
  }

  const intrinsic = Math.max(0, price - tr.basisPerShare);
  const rate = tr.intrinsicOrdinary ? tr.intrinsicRate : tr.gainRate;
  return tr.shares * (intrinsic - intrinsic * rate);
}

function trancheOrdinaryAtVest(tr: Tranche): number {
  return tr.fullValue && tr.countVestOrdinary ? tr.shares * tr.ordinaryPerShareAtVest : 0;
}

// ═══════════════════════════════════════════════════════════════════
// INTERNAL: build tranches per instrument
// ═══════════════════════════════════════════════════════════════════

function priceAt(settings: PortfolioSettings, yr: number): number {
  return settings.currentPrice * Math.pow(1 + settings.annualGrowthPct, yr);
}

/** Spread unvested shares over the schedule, returning [{ vestYr, shares }]. */
function vestSchedule(
  shares: number,
  fields: VestingFields,
  horizon: number
): Array<{ vestYr: number; shares: number }> {
  const arr = buildVestSchedule(shares, fields.vestYears, fields.cliffMonths, fields.vestFrequency, horizon);
  const out: Array<{ vestYr: number; shares: number }> = [];
  let scheduled = 0;
  for (let i = 0; i < arr.length; i += 1) {
    if (arr[i] > 0) {
      out.push({ vestYr: i + 1, shares: arr[i] });
      scheduled += arr[i];
    }
  }
  // If the vesting period runs longer than the projection horizon, some
  // shares never get a vest event inside `arr` (which only covers years
  // 1..horizon). Track them as a tranche vesting just after the horizon so
  // they show up as unvested for the entire visible window, instead of
  // silently disappearing from the share count.
  const stillUnvested = shares - scheduled;
  if (stillUnvested > 0.01) {
    out.push({ vestYr: horizon + 1, shares: stillUnvested });
  }
  return out;
}

function buildHolding(holding: Holding, settings: PortfolioSettings, rates: TaxRates): BuiltHolding {
  const horizon = settings.horizon;
  const tranches: Tranche[] = [];
  const ordinaryEvents: Array<{ yr: number; amount: number }> = [];

  switch (holding.kind) {
    case 'rsu': {
      if (holding.vestedShares > 0) {
        tranches.push({
          vestYr: 0,
          shares: holding.vestedShares,
          basisPerShare: holding.vestedBasisPerShare,
          fullValue: true,
          ordinaryPerShareAtVest: 0,
          countVestOrdinary: false,
          gainRate: rates.ltcg,
          intrinsicOrdinary: false,
          intrinsicRate: 0
        });
      }
      for (const v of vestSchedule(holding.unvestedShares, holding, horizon)) {
        const basis = priceAt(settings, v.vestYr);
        tranches.push({
          vestYr: v.vestYr,
          shares: v.shares,
          basisPerShare: basis,
          fullValue: true,
          ordinaryPerShareAtVest: basis * rates.ord,
          countVestOrdinary: true,
          gainRate: rates.ltcg,
          intrinsicOrdinary: false,
          intrinsicRate: 0
        });
      }
      break;
    }

    case 'option': {
      const isQualifyingLtcg = holding.optionType === 'iso' && holding.holdForLtcg;
      const gainRate = holding.qsbsEligible && isQualifyingLtcg ? rates.isoStateCity : rates.ltcg;
      const mk = (vestYr: number, shares: number): Tranche => ({
        vestYr,
        shares,
        basisPerShare: holding.strike,
        fullValue: false,
        ordinaryPerShareAtVest: 0,
        countVestOrdinary: false,
        gainRate,
        // NSO and disqualifying ISO: whole spread ordinary. Qualifying ISO: capital gains.
        intrinsicOrdinary: !isQualifyingLtcg,
        intrinsicRate: rates.ord
      });
      if (holding.vestedShares > 0) tranches.push(mk(0, holding.vestedShares));
      for (const v of vestSchedule(holding.unvestedShares, holding, horizon)) {
        tranches.push(mk(v.vestYr, v.shares));
      }
      break;
    }

    case 'espp': {
      const start = Math.max(1, Math.floor(holding.startYear));
      const end = Math.min(horizon, Math.floor(holding.endYear));
      const annualContribution = holding.contributionPerPeriod * holding.periodsPerYear;
      for (let p = start; p <= end; p += 1) {
        const offeringPrice = priceAt(settings, p - 1);
        const purchaseFmv = priceAt(settings, p);
        const refPrice = holding.lookback ? Math.min(offeringPrice, purchaseFmv) : purchaseFmv;
        const purchasePrice = refPrice * (1 - holding.discountPct);
        if (purchasePrice <= 0) continue;
        const shares = annualContribution / purchasePrice;
        tranches.push({
          vestYr: p,
          shares,
          basisPerShare: purchasePrice,
          fullValue: true,
          ordinaryPerShareAtVest: 0,
          countVestOrdinary: false,
          gainRate: rates.ltcg,
          intrinsicOrdinary: false,
          intrinsicRate: 0,
          espp: {
            purchasePrice,
            purchaseFmv,
            offeringPrice,
            discountPct: holding.discountPct,
            qualifying: holding.qualifying
          }
        });
      }
      break;
    }

    case 'rsa': {
      const basis = holding.grantPricePerShare;
      if (holding.election83b) {
        // Ordinary income recognized once, at grant, on (FMV − purchase price).
        const ordinary = Math.max(0, basis - holding.purchasePricePerShare) * holding.totalShares * rates.ord;
        if (ordinary > 0) ordinaryEvents.push({ yr: 0, amount: ordinary });
      }
      for (const v of vestSchedule(holding.totalShares, holding, horizon)) {
        if (holding.election83b) {
          tranches.push({
            vestYr: v.vestYr,
            shares: v.shares,
            basisPerShare: basis,
            fullValue: true,
            ordinaryPerShareAtVest: 0,
            countVestOrdinary: false,
            gainRate: rates.ltcg,
            intrinsicOrdinary: false,
            intrinsicRate: 0
          });
        } else {
          // No election: ordinary at each vest on (FMV_vest − purchase price).
          const fmvVest = priceAt(settings, v.vestYr);
          tranches.push({
            vestYr: v.vestYr,
            shares: v.shares,
            basisPerShare: fmvVest,
            fullValue: true,
            ordinaryPerShareAtVest: Math.max(0, fmvVest - holding.purchasePricePerShare) * rates.ord,
            countVestOrdinary: true,
            gainRate: rates.ltcg,
            intrinsicOrdinary: false,
            intrinsicRate: 0
          });
        }
      }
      break;
    }

    case 'sar': {
      const effectiveTarget = holding.targetShares * holding.performanceMultiplier;
      for (const v of vestSchedule(effectiveTarget, holding, horizon)) {
        if (holding.settlement === 'full-value') {
          // PSU: taxed like an RSU on full FMV at vest.
          const basis = priceAt(settings, v.vestYr);
          tranches.push({
            vestYr: v.vestYr,
            shares: v.shares,
            basisPerShare: basis,
            fullValue: true,
            ordinaryPerShareAtVest: basis * rates.ord,
            countVestOrdinary: true,
            gainRate: rates.ltcg,
            intrinsicOrdinary: false,
            intrinsicRate: 0
          });
        } else {
          // SAR / phantom: appreciation above baseline, taxed as ordinary income.
          tranches.push({
            vestYr: v.vestYr,
            shares: v.shares,
            basisPerShare: holding.baselinePrice,
            fullValue: false,
            ordinaryPerShareAtVest: 0,
            countVestOrdinary: false,
            gainRate: rates.ltcg,
            intrinsicOrdinary: true,
            intrinsicRate: rates.ord
          });
        }
      }
      break;
    }
  }

  return { tranches, ordinaryEvents };
}

// ═══════════════════════════════════════════════════════════════════
// PROJECTION
// ═══════════════════════════════════════════════════════════════════

function projectHolding(holding: Holding, settings: PortfolioSettings, rates: TaxRates): HoldingProjection {
  const { tranches, ordinaryEvents } = buildHolding(holding, settings, rates);
  const totalShares = tranches.reduce((s, t) => s + t.shares, 0);
  const years: HoldingYear[] = [];

  for (let yr = 0; yr <= settings.horizon; yr += 1) {
    const price = priceAt(settings, yr);
    let vestedShares = 0;
    let unvestedShares = 0;
    let vestedGross = 0;
    let unvestedGross = 0;
    let vestedNet = 0;
    let ordinaryTaxThisYr = 0;

    for (const tr of tranches) {
      if (tr.vestYr <= yr) {
        vestedShares += tr.shares;
        vestedGross += trancheGross(tr, price);
        vestedNet += trancheNet(tr, price, rates);
      } else {
        unvestedShares += tr.shares;
        unvestedGross += trancheGross(tr, price);
      }
      if (tr.vestYr === yr) ordinaryTaxThisYr += trancheOrdinaryAtVest(tr);
    }
    for (const ev of ordinaryEvents) {
      if (ev.yr === yr) ordinaryTaxThisYr += ev.amount;
    }

    years.push({
      yr,
      price,
      vestedShares,
      unvestedShares,
      vestedGross,
      unvestedGross,
      vestedNet,
      ordinaryTaxThisYr
    });
  }

  return {
    id: holding.id,
    label: holding.label,
    kind: holding.kind,
    color: holding.color,
    totalShares,
    years
  };
}

/**
 * Project every holding and aggregate into a portfolio timeline (years 0..horizon).
 */
export function buildPortfolio(holdings: Holding[], settings: PortfolioSettings): PortfolioResult {
  const rates = resolveTaxRates(settings.taxPreset, settings.filingStatus, settings.customRates);
  const projections = holdings.map((h) => projectHolding(h, settings, rates));

  const portfolio: PortfolioYear[] = [];
  for (let yr = 0; yr <= settings.horizon; yr += 1) {
    let vestedGross = 0;
    let unvestedGross = 0;
    let vestedNet = 0;
    let ordinaryTaxThisYr = 0;
    for (const proj of projections) {
      const y = proj.years[yr];
      vestedGross += y.vestedGross;
      unvestedGross += y.unvestedGross;
      vestedNet += y.vestedNet;
      ordinaryTaxThisYr += y.ordinaryTaxThisYr;
    }
    portfolio.push({
      yr,
      price: priceAt(settings, yr),
      vestedGross,
      unvestedGross,
      totalGross: vestedGross + unvestedGross,
      vestedNet,
      ordinaryTaxThisYr
    });
  }

  return {
    horizon: settings.horizon,
    currentPrice: settings.currentPrice,
    annualGrowthPct: settings.annualGrowthPct,
    rates,
    holdings: projections,
    portfolio
  };
}

export const INSTRUMENT_LABELS: Record<InstrumentKind, string> = {
  rsu: 'RSUs',
  option: 'Stock options',
  espp: 'ESPP',
  rsa: 'Restricted stock (RSA)',
  sar: 'SAR / phantom / PSU'
};
